import { useState, useEffect, useMemo } from "react";
import { festivalDayIso, formatTimeWithSetting } from "@produce/shared";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Loader2,
  MessageSquarePlus,
  Search,
  X,
  MapPin,
  Eye,
  EyeOff,
  ChevronUp,
  SlidersHorizontal,
  RotateCw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  ReadOnlyScheduleTimeline,
  type FeedbackAnchor,
  type FeedbackMarker,
} from "@/components/ReadOnlyScheduleTimeline";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { scheduleModeLabel, type ScheduleShareMode } from "@/lib/scheduleShare";
import type { Json } from "@/integrations/supabase/types";

// Shape returned by the schedule-share edge function.
interface SharePayload {
  success: true;
  mode: ScheduleShareMode;
  allow_feedback: boolean;
  festival_name: string | null;
  festival_date_start: string | null;
  festival_date_end: string | null;
  briefing: { id: string; stages: Json | null; doors_open: string | null };
  lineup: any[];
  assignments: any[];
  crewMembers: any[];
  stages: any[];
  dayDoors: any[];
  timeFormat: "24h" | "12h";
  feedback: Array<{
    id: string;
    stage_name: string | null;
    anchor_time: string | null;
    artist_name: string | null;
    author_name: string;
    body: string;
    status: string;
    event_date?: string | null;
  }>;
}

/**
 * Public, unauthenticated client-review page. The producer shares a secret link
 * (/schedule-review?token=...). We exchange the token for a read-only snapshot of
 * the schedule via the schedule-share edge function and render the SAME timeline
 * the producer uses (in shareMode). Both share modes — "Published" and
 * "Unpublished" — use this one page; only the label and feedback rights differ.
 *
 * Clients leave feedback by clicking a spot on the schedule (scheduled or empty),
 * or by searching for an artist to jump to. The layout stretches full width and
 * is phone-friendly (rotate-to-landscape prompt on small portrait screens).
 */
export default function ScheduleReview() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const token = searchParams.get("token");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [artistSearch, setArtistSearch] = useState("");
  const [showFeedback, setShowFeedback] = useState(true);
  const [feedbackAnchor, setFeedbackAnchor] = useState<FeedbackAnchor | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    id: string;
    author_name: string;
    body: string;
  } | null>(null);
  // The header bar (search / feedback controls / tip) collapses so the timeline
  // can use the full screen — collapsed by default on phones, open on desktop.
  const [controlsOpen, setControlsOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 768,
  );

  const {
    data: payload,
    isLoading,
    isError,
  } = useQuery<SharePayload>({
    queryKey: ["schedule-share", token],
    enabled: !!token,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("schedule-share", {
        body: { token },
      });
      if (error || !data?.success) {
        throw new Error(data?.error || "This link is no longer available");
      }
      return data as SharePayload;
    },
  });

  const eventDays = useMemo(() => {
    if (!payload?.festival_date_start) return [] as string[];
    const start = new Date(payload.festival_date_start);
    const end = payload.festival_date_end ? new Date(payload.festival_date_end) : start;
    const days: string[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(format(new Date(d), "yyyy-MM-dd"));
    }
    return days;
  }, [payload?.festival_date_start, payload?.festival_date_end]);

  useEffect(() => {
    if (eventDays.length > 0 && !selectedDay) {
      const today = festivalDayIso();
      setSelectedDay(eventDays.includes(today) ? today : eventDays[0]);
    }
  }, [eventDays, selectedDay]);

  const assignmentsForDay = useMemo(
    () =>
      (payload?.assignments ?? [])
        .filter((a) => a.event_date === selectedDay)
        .map((d) => ({
          ...d,
          crew_member_id: d.crew_member_id ?? "",
          artist_name: d.artist_name ?? undefined,
          assignment_type: (d.assignment_type as "artist" | "custom") ?? "custom",
          subject: d.subject ?? undefined,
          notes: d.notes ?? undefined,
        })),
    [payload?.assignments, selectedDay],
  );
  const crewForDay = useMemo(
    () => (payload?.crewMembers ?? []).filter((c) => c.event_date === selectedDay),
    [payload?.crewMembers, selectedDay],
  );

  // The client sees their own link's feedback (persisted) as markers, with the
  // producer's status (open red "!" / resolved green check). Read-only here.
  const feedbackMarkers: FeedbackMarker[] = useMemo(
    () =>
      (payload?.feedback ?? [])
        .filter(
          (f) => f.stage_name && f.anchor_time && (!f.event_date || f.event_date === selectedDay),
        )
        .map((f) => ({
          id: f.id,
          stage_name: f.stage_name,
          anchor_time: f.anchor_time,
          author_name: f.author_name,
          body: f.body,
          status: f.status,
        })),
    [payload?.feedback, selectedDay],
  );

  const refreshFeedback = () =>
    queryClient.invalidateQueries({ queryKey: ["schedule-share", token] });

  const openFeedback = (anchor: FeedbackAnchor | null) => {
    setEditTarget(null);
    setFeedbackAnchor(anchor);
    setFeedbackOpen(true);
  };

  // Client edits their own feedback: prefill the dialog and switch it to update.
  const editFeedback = (m: FeedbackMarker) => {
    setEditTarget({ id: m.id, author_name: m.author_name, body: m.body });
    setFeedbackAnchor({
      stage_name: m.stage_name ?? "",
      anchor_time: m.anchor_time ?? "",
      artist_name: null,
    });
    setFeedbackOpen(true);
  };

  // Client deletes their own feedback (token-scoped on the server).
  const deleteFeedback = async (id: string) => {
    const { data, error } = await supabase.functions.invoke("schedule-share-feedback", {
      body: { token, action: "delete", feedback_id: id },
    });
    if (error || !data?.success) {
      toast.error("Could not delete feedback");
      return;
    }
    toast.success("Feedback deleted");
    refreshFeedback();
  };

  if (!token) {
    return <CenteredMessage title="Invalid link" body="This link is missing an access code." />;
  }
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isError || !payload) {
    return (
      <CenteredMessage
        title="Link unavailable"
        body="This share link has been revoked or no longer exists. Please ask the producer for a new link."
      />
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* On a phone in portrait, the wide timeline reads far better sideways. */}
      <RotateDevicePrompt />

      {/* Header (z above the timeline's sticky stage-label column so it never
          peeks over the title when scrolling). */}
      <div className="sticky top-0 z-[60] border-b border-border bg-background px-4 py-2 sm:px-6">
        {/* Always-visible slim strip: name + status + a toggle to free up the
            screen. The timeline itself stays the only schedule view. */}
        <div className="flex items-center gap-2">
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold sm:text-xl">
            {payload.festival_name || "Schedule"}
          </h1>
          <Badge variant={payload.mode === "published" ? "default" : "secondary"}>
            {scheduleModeLabel(payload.mode)}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setControlsOpen((v) => !v)}
            title={controlsOpen ? "Hide controls" : "Show controls"}
          >
            {controlsOpen ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <SlidersHorizontal className="h-4 w-4" />
            )}
            <span className="ml-2 hidden sm:inline">{controlsOpen ? "Hide" : "Controls"}</span>
          </Button>
        </div>

        {/* Collapsible controls: feedback actions, artist search, tip. */}
        {controlsOpen && (
          <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={artistSearch}
                onChange={(e) => setArtistSearch(e.target.value)}
                placeholder="Search an artist…"
                className="pl-9 pr-9"
              />
              {artistSearch && (
                <button
                  type="button"
                  onClick={() => setArtistSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowFeedback((v) => !v)}>
                {showFeedback ? (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Hide feedback
                  </>
                ) : (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Show feedback
                  </>
                )}
              </Button>
              {payload.allow_feedback && (
                <Button variant="outline" size="sm" onClick={() => openFeedback(null)}>
                  <MessageSquarePlus className="mr-2 h-4 w-4" />
                  Leave feedback
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Tip (only when controls open, and feedback allowed). */}
        {controlsOpen && payload.allow_feedback && (
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            Tip: click any spot on the schedule to leave feedback there.
          </p>
        )}
      </div>

      {/* Timeline — full width */}
      <div className="flex-1 overflow-x-hidden p-4 sm:p-6">
        <ReadOnlyScheduleTimeline
          briefing={payload.briefing}
          lineup={payload.lineup}
          assignments={assignmentsForDay}
          crewMembers={crewForDay}
          eventDays={eventDays}
          selectedDay={selectedDay}
          onDayChange={setSelectedDay}
          shareMode
          stagesOverride={payload.stages}
          dayDoorsOverride={payload.dayDoors}
          timeFormatOverride={payload.timeFormat}
          highlightArtistName={artistSearch}
          onAddFeedback={payload.allow_feedback ? openFeedback : undefined}
          feedbackMarkers={showFeedback ? feedbackMarkers : []}
          onEditFeedback={payload.allow_feedback ? editFeedback : undefined}
          onDeleteFeedback={payload.allow_feedback ? deleteFeedback : undefined}
        />
      </div>

      <FeedbackDialog
        timeFormat={payload.timeFormat}
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        token={token}
        anchor={feedbackAnchor}
        selectedDay={selectedDay}
        editTarget={editTarget}
        onSubmitted={refreshFeedback}
      />
    </div>
  );
}

/**
 * Full-screen overlay shown only on small screens in portrait, asking the viewer
 * to rotate to landscape (the timeline is far clearer wide). Pure CSS visibility
 * via Tailwind `landscape:`/`md:` variants — appears/disappears as the device
 * rotates, no JS listeners.
 */
function RotateDevicePrompt() {
  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-center gap-4 bg-background p-8 text-center md:hidden landscape:hidden">
      <RotateCw className="h-14 w-14 animate-pulse text-primary" />
      <h2 className="text-lg font-semibold">Rotate your device</h2>
      <p className="max-w-xs text-sm text-muted-foreground">
        Turn your phone sideways to view the full schedule clearly.
      </p>
    </div>
  );
}

function CenteredMessage({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{body}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function FeedbackDialog({
  open,
  onOpenChange,
  token,
  anchor,
  selectedDay,
  editTarget,
  onSubmitted,
  timeFormat,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  anchor: FeedbackAnchor | null;
  selectedDay: string;
  editTarget: { id: string; author_name: string; body: string } | null;
  onSubmitted: () => void;
  /**
   * The producer's 12h/24h setting, travelling with the share payload —
   * this page has no authenticated producer to read it from.
   */
  timeFormat: "24h" | "12h";
}) {
  const [authorName, setAuthorName] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!editTarget;

  // On open: prefill from the edited item, or clear the comment for a new one.
  useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setAuthorName(editTarget.author_name);
      setBody(editTarget.body);
    } else {
      setBody("");
    }
  }, [open, editTarget]);

  const anchorLabel = anchor
    ? [
        anchor.stage_name,
        anchor.anchor_time ? formatTimeWithSetting(anchor.anchor_time, timeFormat) : null,
        anchor.artist_name,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authorName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!body.trim()) {
      toast.error("Please write a comment");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("schedule-share-feedback", {
        body: isEdit
          ? {
              token,
              action: "update",
              feedback_id: editTarget!.id,
              author_name: authorName.trim(),
              body: body.trim(),
            }
          : {
              token,
              author_name: authorName.trim(),
              body: body.trim(),
              event_date: selectedDay || null,
              stage_name: anchor?.stage_name ?? null,
              anchor_time: anchor?.anchor_time ?? null,
              artist_name: anchor?.artist_name ?? null,
            },
      });
      if (error || !data?.success) {
        throw new Error(data?.error || "Could not send feedback");
      }
      toast.success(
        isEdit ? "Feedback updated." : "Thanks! Your feedback was sent to the producer.",
      );
      onSubmitted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] gap-3 overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit feedback" : "Leave feedback"}</DialogTitle>
          <DialogDescription>
            {anchorLabel
              ? "Your comment is linked to the spot you clicked on the schedule."
              : "Leave a general comment about this schedule."}
          </DialogDescription>
        </DialogHeader>

        {anchorLabel && (
          <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
            <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
            {anchorLabel}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="feedback-name">Your name</Label>
            <Input
              id="feedback-name"
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              placeholder="e.g. Sanne (organisation)"
              maxLength={80}
              disabled={submitting}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-body">Comment</Label>
            <Textarea
              id="feedback-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g. Can this crew start an hour earlier on the main stage?"
              rows={2}
              maxLength={2000}
              disabled={submitting}
              required
              className="max-h-[30vh]"
            />
          </div>
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEdit ? "Saving…" : "Sending…"}
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Send feedback"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
